import React, { useRef, useState } from 'react';
import { Modal, Nav, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileImport, faKey, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { useToast } from 'providers/ToastProvider';
import {
  useAdicionarReferenciaImportadaXml,
  useAdicionarReferenciaImportadaChave,
} from 'hooks/useNFe';

interface Props {
  emissaoId: string;
}

type Tab = 'xml' | 'chave';

const BotaoAdicionarReferenciaNFe: React.FC<Props> = ({ emissaoId }) => {
  const { showToast } = useToast();
  const [show, setShow] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('xml');
  const [chave, setChave] = useState('');
  const [chaveError, setChaveError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const xmlMutation = useAdicionarReferenciaImportadaXml(emissaoId);
  const chaveMutation = useAdicionarReferenciaImportadaChave(emissaoId);

  const isPending = xmlMutation.isPending || chaveMutation.isPending;

  const handleOpen = () => {
    setChave('');
    setChaveError('');
    setActiveTab('xml');
    setShow(true);
  };

  const handleClose = () => {
    if (!isPending) setShow(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    xmlMutation.mutate(file, {
      onSuccess: (data) => {
        showToast(
          `${data.nItensAdicionados} item(s) adicionados da nota ${data.chaveReferenciada.slice(-9)}.`,
          'success'
        );
        setShow(false);
      },
      onError: (error: any) => {
        const msg =
          error?.response?.data?.message ??
          'Erro ao importar XML. Verifique se é uma NF-e autorizada do mesmo fornecedor.';
        showToast(msg, 'error');
      },
    });
    e.target.value = '';
  };

  const handleChaveSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (chave.length !== 44) {
      setChaveError('A chave de acesso deve ter exatamente 44 dígitos');
      return;
    }
    chaveMutation.mutate(chave, {
      onSuccess: (data) => {
        showToast(
          `${data.nItensAdicionados} item(s) adicionados da nota ${data.chaveReferenciada.slice(-9)}.`,
          'success'
        );
        setShow(false);
      },
      onError: (error: any) => {
        const msg =
          error?.response?.data?.message ??
          'Erro ao importar por chave. Verifique a chave e tente novamente.';
        setChaveError(msg);
      },
    });
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xml,application/xml,text/xml"
        className="d-none"
        onChange={handleFileChange}
      />

      <Button
        variant="phoenix-primary"
        size="sm"
        onClick={handleOpen}
        className="d-flex align-items-center"
      >
        <FontAwesomeIcon icon={faPlus} className="me-1" />
        Adicionar nota referenciada
      </Button>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton={!isPending}>
          <Modal.Title className="fs-6 fw-semibold">Adicionar nota referenciada</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-body-secondary small mb-3">
            Importe outra NF-e do mesmo fornecedor para consolidar em uma única devolução.
            Os itens serão adicionados à nota atual.
          </p>

          <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={(k) => setActiveTab(k as Tab)}>
            <Nav.Item>
              <Nav.Link eventKey="xml" disabled={isPending}>
                <FontAwesomeIcon icon={faFileImport} className="me-1" />
                XML
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="chave" disabled={isPending}>
                <FontAwesomeIcon icon={faKey} className="me-1" />
                Chave de Acesso
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {activeTab === 'xml' && (
            <div>
              <p className="text-body-secondary small">
                Selecione o arquivo XML da NF-e autorizada.
              </p>
              <Button
                variant="outline-secondary"
                onClick={() => inputRef.current?.click()}
                disabled={isPending}
                className="w-100"
              >
                {xmlMutation.isPending ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    Importando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faFileImport} className="me-2" />
                    Selecionar XML
                  </>
                )}
              </Button>
            </div>
          )}

          {activeTab === 'chave' && (
            <form id="chave-form" onSubmit={handleChaveSubmit}>
              <div>
                <label className="form-label small fw-medium">Chave de Acesso</label>
                <Form.Control
                  type="text"
                  className="font-monospace"
                  placeholder="00000000000000000000000000000000000000000000"
                  value={chave}
                  onChange={(e) => {
                    setChave(e.target.value.replace(/\D/g, '').slice(0, 44));
                    if (chaveError) setChaveError('');
                  }}
                  maxLength={44}
                  isInvalid={!!chaveError}
                  autoFocus
                  disabled={chaveMutation.isPending}
                />
                {chaveError && <div className="invalid-feedback d-block">{chaveError}</div>}
                <div className="form-text">{chave.length}/44 dígitos</div>
              </div>
            </form>
          )}
        </Modal.Body>

        {activeTab === 'chave' && (
          <Modal.Footer>
            <Button variant="phoenix-secondary" onClick={handleClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button
              type="submit"
              form="chave-form"
              variant="primary"
              disabled={isPending || chave.length !== 44}
            >
              {chaveMutation.isPending ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  Buscando...
                </>
              ) : (
                'Importar'
              )}
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  );
};

export default BotaoAdicionarReferenciaNFe;
